
import { defineComponent, ref, reactive, computed, onMounted } from 'vue';
import dayjs from 'dayjs';
import { message, Modal, Drawer } from 'ant-design-vue';
import { ReloadOutlined } from '@ant-design/icons-vue';
import { OioPagination, OioForm, OioFormItem, OioInput, OioButton, OioTag } from '@kunlun/vue-ui-antd';
import { BillingBill, BillingBillStatus, PageQueryParams } from '../types';
import { queryBillingBillPage, querySellerBillingBillPage, queryMerchantBillingBillPage, confirmBillingBill, exportBillingLines, downloadInvoiceZip } from '../api/billingBill';
import { useBillingStatus } from '../hooks/useBillingStatus';
import { useBillingAmount } from '../hooks/useBillingAmount';
import { usePortalType } from '../hooks/usePortalType';
import ImportInvoiceContent from './ImportInvoiceContent.vue';
import OperationLogDialog from './OperationLogDialog.vue';
import BillingBillDetailPage from './BillingBillDetailPage.vue';

export default defineComponent({
  name: 'BillingBillListPage',
  components: { ReloadOutlined, Drawer, ImportInvoiceContent, OperationLogDialog, BillingBillDetailPage, OioPagination, OioForm, OioFormItem, OioInput, OioButton, OioTag },
  setup() {
    const { getBillStatusText, getBillStatusColor, canConfirm, canEdit, canImport, canDownloadInvoice } =
      useBillingStatus();
    const { formatAmount, getAmountClass } = useBillingAmount();
    const { isDistributor, isSeller } = usePortalType();

    const loading = ref(false);
    const batchLoading = ref(false);
    const tableData = ref<BillingBill[]>([]);
    const selectedRowKeys = ref<number[]>([]);
    const importVisible = ref(false);
    const logVisible = ref(false);
    const currentLogBill = ref<BillingBill | null>(null);
    const detailVisible = ref(false);
    const detailBillId = ref(0);
    const detailMode = ref<'view' | 'edit'>('view');

    const tableRef = ref();

    onMounted(() => {
      fetchData();
    });

    const searchForm = reactive({
      code: '',
      distributorName: '',
      distributorErpCode: [] as string[],
      salesOrgCode: '',
      salesOrgName: '',
      status: undefined as string | undefined,
      dateRange: null as any
    });

    const pagination = reactive({
      current: 1,
      pageSize: 15,
      total: 0
    });

    const selectedPendingBills = computed(() => {
      return tableData.value.filter(
        (bill) => selectedRowKeys.value.includes(bill.id) && bill.status === BillingBillStatus.PENDING
      );
    });

    const onCheckboxChange = ({ records }: any) => {
      selectedRowKeys.value = (records || []).map((r: BillingBill) => r.id);
    };

    const buildRsql = (): string => {
      const conditions: string[] = ['(1==1)'];
      if (searchForm.code) conditions.push(`code=='${searchForm.code}'`);
      if (searchForm.distributorName) conditions.push(`distributorName=like='%${searchForm.distributorName}%'`);
      if (searchForm.distributorErpCode.length > 0) {
        const codes = searchForm.distributorErpCode.map((c) => `'${c.trim()}'`).filter((c) => c !== "''");
        if (codes.length > 0) conditions.push(`distributorErpCode=in=(${codes.join(',')})`);
      }
      if (searchForm.salesOrgCode) conditions.push(`salesOrgCode=='${searchForm.salesOrgCode}'`);
      if (searchForm.salesOrgName) conditions.push(`salesOrgName=like='%${searchForm.salesOrgName}%'`);
      if (searchForm.status) conditions.push(`status=='${searchForm.status}'`);
      if (searchForm.dateRange && searchForm.dateRange[0]) {
        conditions.push(`createDate=ge='${dayjs(searchForm.dateRange[0]).format('YYYY-MM-DD')} 00:00:00'`);
        conditions.push(`createDate=le='${dayjs(searchForm.dateRange[1]).format('YYYY-MM-DD')} 23:59:59'`);
      }
      return conditions.join(' and ');
    };

    const fetchData = async () => {
      loading.value = true;
      try {
        const params: PageQueryParams = {
          currentPage: pagination.current,
          size: pagination.pageSize,
          rsql: buildRsql(),
          queryData: { sortBy: 'createDate', order: 'DESC' }
        };
        const result = isDistributor.value
          ? await queryMerchantBillingBillPage(params)
          : isSeller.value
            ? await querySellerBillingBillPage(params)
            : await queryBillingBillPage(params);
        tableData.value = result.content || [];
        pagination.total = result.totalElements || 0;
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.current = 1;
      fetchData();
    };

    const handleReset = () => {
      searchForm.code = '';
      searchForm.distributorName = '';
      searchForm.distributorErpCode = [];
      searchForm.salesOrgCode = '';
      searchForm.salesOrgName = '';
      searchForm.status = undefined;
      searchForm.dateRange = null;
      pagination.current = 1;
      fetchData();
    };

    const handlePaginationChange = (page: number, pageSize: number) => {
      pagination.current = page;
      pagination.pageSize = pageSize;
      fetchData();
    };

    const onSelectChange = (keys: number[]) => {
      selectedRowKeys.value = keys;
    };

    /**
     * 详情 / 部分开票改为弹窗内嵌详情组件。
     * 原本通过 router.push 跳转独立 FORM 视图（action=`${prefix}BillingBillDetail`），
     * 需在平台为该 action 配置两端菜单/权限，易出现无权访问。
     * 改为弹窗后详情组件直接在当前列表页渲染，复用列表页权限，规避该问题。
     */
    const openDetail = (record: BillingBill, mode: 'view' | 'edit') => {
      detailBillId.value = record.id;
      detailMode.value = mode;
      detailVisible.value = true;
    };

    const handleDetail = (record: BillingBill) => openDetail(record, 'view');

    const handleEdit = (record: BillingBill) => openDetail(record, 'edit');

    const handleDetailClose = () => {
      detailVisible.value = false;
      fetchData();
    };

    const handleConfirm = async (record: BillingBill) => {
      Modal.confirm({
        title: '整单开票',
        content: `确定要对开票单 ${record.code} 执行整单开票吗？`,
        onOk: async () => {
          try {
            await confirmBillingBill(record.id);
            message.success('整单开票已提交');
            fetchData();
          } catch (e: any) {
            // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
          }
        }
      });
    };

    const handleBatchConfirm = async () => {
      if (selectedPendingBills.value.length === 0) {
        message.warning('请勾选待确认的开票单');
        return;
      }
      Modal.confirm({
        title: '批量整单开票',
        content: `确定要批量整单开票选中的 ${selectedPendingBills.value.length} 张开票单吗？`,
        onOk: async () => {
          batchLoading.value = true;
          try {
            const bills = selectedPendingBills.value;
            const results = await Promise.allSettled(
              bills.map(bill => confirmBillingBill(bill.id))
            );
            const failed = results.filter(r => r.status === 'rejected').length;
            const succeeded = results.length - failed;
            if (failed === 0) {
              message.success(`批量整单开票已提交，共 ${succeeded} 张`);
            } else if (succeeded === 0) {
              message.error(`批量整单开票全部失败，共 ${failed} 张`);
            } else {
              message.warning(`批量整单开票部分成功：成功 ${succeeded} 张，失败 ${failed} 张`);
            }
            selectedRowKeys.value = [];
            fetchData();
          } catch (e: any) {
            // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
          } finally {
            batchLoading.value = false;
          }
        }
      });
    };

    const handleImport = () => {
      importVisible.value = true;
    };

    /** 行级导出明细：导出该开票单关联单据 Excel 并下载 */
    const handleExportLines = async (record: BillingBill) => {
      try {
        const url = await exportBillingLines(record.id, []);
        if (url) {
          window.open(url, '_blank');
        } else {
          message.warning('导出地址获取失败');
        }
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      }
    };

    /** 批量导出明细：导出勾选的所有开票单关联单据 Excel 并下载 */
    const exportLoading = ref(false);
    const handleBatchExport = async () => {
      if (selectedRowKeys.value.length === 0) {
        message.warning('请先勾选需要导出的开票单');
        return;
      }
      const selectedBills = tableData.value.filter((bill) => selectedRowKeys.value.includes(bill.id));
      if (selectedBills.length === 0) {
        message.warning('未找到已勾选的开票单');
        return;
      }
      exportLoading.value = true;
      try {
        let successCount = 0;
        for (const bill of selectedBills) {
          const url = await exportBillingLines(bill.id, []);
          if (url) {
            window.open(url, '_blank');
            successCount++;
          }
        }
        if (successCount === 0) {
          message.warning('导出地址获取失败，请重试');
        } else if (successCount < selectedBills.length) {
          message.warning(`部分导出成功：成功 ${successCount} 张，失败 ${selectedBills.length - successCount} 张`);
        } else {
          message.success(`导出成功，共 ${successCount} 张`);
        }
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        exportLoading.value = false;
      }
    };

    /** 下载发票：后端打包 zip 后返回下载地址 */
    const handleDownload = async (record: BillingBill) => {
      try {
        const url = await downloadInvoiceZip(record.id);
        if (url) {
          window.open(url, '_blank');
          message.success('发票打包下载已开始');
        } else {
          message.warning('未找到可下载的发票文件');
        }
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      }
    };

    /** 打开操作日志弹窗 */
    const handleLog = (record: BillingBill) => {
      currentLogBill.value = record;
      logVisible.value = true;
    };

    const formatDate = (dateStr: string) => {
      if (!dateStr) return '-';
      return dateStr.substring(0, 10);
    };

    return {
      loading,
      batchLoading,
      tableData,
      selectedRowKeys,
      searchForm,
      pagination,
      selectedPendingBills,
      importVisible,
      logVisible,
      currentLogBill,
      detailVisible,
      detailBillId,
      detailMode,
      isDistributor,
      isSeller,
      tableRef,
      getBillStatusText,
      getBillStatusColor,
      canConfirm,
      canEdit,
      canImport,
      canDownloadInvoice,
      formatAmount,
      getAmountClass,
      formatDate,
      fetchData,
      handleSearch,
      handleReset,
      handlePaginationChange,
      onCheckboxChange,
      onSelectChange,
      handleDetail,
      handleEdit,
      handleDetailClose,
      handleConfirm,
      handleBatchConfirm,
      handleImport,
      handleExportLines,
      handleBatchExport,
      exportLoading,
      handleDownload,
      handleLog
    };
  }
});
